import type { ComponentProps } from 'react';
import React, { useMemo } from 'react';
import { useResponsive } from '../../hooks';
import { Picker } from '../Pickers/Picker';
import { PickersGroup } from '../Pickers/PickersGroup';
import { Select } from '../Select';

export const ResponsiveSelect = (props: ResponsiveSelectProps) => {
  const { submitBtnLabel, options, onSelect, testID, ...selectProps } = props;

  const { currentBreakpoint } = useResponsive();
  const shouldUsePicker = currentBreakpoint.isMobile;
  const optionsHasGroups = !Array.isArray(options);

  // ────────────────────────────────────────────────────────────────────

  const normalizedOptions = useMemo(() => {
    const optionsAsGroups = options as PickersGroupOptions;

    if (shouldUsePicker) {
      return optionsHasGroups
        ? removeDisabledOptionsFromGroups(optionsAsGroups)
        : removeDisabledOptions(options);
    }

    return optionsHasGroups
      ? extractOptionsFromGroups(optionsAsGroups)
      : Object.values(options).flat();
  }, [options, optionsHasGroups, shouldUsePicker]);

  // ────────────────────────────────────────────────────────────────────

  if (shouldUsePicker) {
    if (optionsHasGroups)
      return (
        <PickersGroup
          optionsGroups={normalizedOptions as PickersGroupOptions}
          submitBtnLabel={submitBtnLabel}
          onSubmit={onSelect}
          {...selectProps}
        />
      );

    return (
      <Picker
        options={normalizedOptions as PickerOptions}
        submitBtnLabel={submitBtnLabel}
        onSubmit={onSelect}
        {...selectProps}
      />
    );
  }

  // ────────────────────────────────────────────────────────────────────

  return (
    <Select
      testID={testID}
      options={normalizedOptions as SelectOptions}
      onSelect={onSelect}
      {...selectProps}
    />
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ResponsiveSelectProps = {
  readonly label: string;
  readonly modalTitle?: string;
  readonly value: string | number;
  readonly options: PickersGroupOptions | PickerOptions | SelectOptions;
  readonly submitBtnLabel?: string;
  readonly testID?: string;
  readonly onSelect: PickerOnSubmit | SelectOnSelect;
  readonly isLoading?: boolean;
};

type PickersGroupOptions = ComponentProps<typeof PickersGroup>['optionsGroups'];
type PickerOptions = ComponentProps<typeof Picker>['options'];
type PickerOnSubmit = ComponentProps<typeof Picker>['onSubmit'];

type SelectOptions = ComponentProps<typeof Select>['options'];
type SelectOnSelect = ComponentProps<typeof Select>['onSelect'];

// ─── UTILS ──────────────────────────────────────────────────────────────────────

export const extractOptionsFromGroups = (
  optionsGroups: PickersGroupOptions,
) => {
  const groups = Object.entries(optionsGroups);

  return groups.reduce<PickerOptions>((allOptions, [groupLabel, options]) => {
    const formattedOptions = options.map((option) => ({
      ...option,
      label: `${groupLabel}, ${option.label}`,
    }));

    return [...allOptions, ...formattedOptions];
  }, []);
};

export const removeDisabledOptionsFromGroups = (
  optionsGroups: PickersGroupOptions,
) => {
  return Object.entries(optionsGroups).reduce(
    (groups, [groupName, options]) => {
      return {
        ...groups,
        [groupName]: (options as SelectOptions).filter(
          (option) => !option.disabled,
        ),
      };
    },
    {},
  );
};

const removeDisabledOptions = (options: SelectOptions) => {
  return options.filter((option) => !option.disabled);
};
